import { useCallback, useEffect, useRef } from "react";
import { useRevalidator } from "@remix-run/react";

import type { RevalidationState } from "@remix-run/router";

export interface UseDataRefreshMessage {
  formAction?: string;
  intent?: string;
}

export interface UseDataRefreshCallbackData extends UseDataRefreshMessage {}

export interface UseDataRefreshOptions {
  name?: string;
  /**
   * Fires when the refresh is initiated.
   */
  onRefreshStart?: (data: UseDataRefreshCallbackData) => void;
  /**
   * Fires when the refresh is complete, spefically once the
   * route revalidator becomes idle again.
   */
  onRefreshEnd?: () => void;
  /**
   * Fires immediately after the route revalidation is requested.
   */
  onRefresh?: (data: UseDataRefreshCallbackData) => void;
}

/**
 * Used to handle requests for data refreshes.
 */
export function useDataRefresh({
  onRefresh,
  onRefreshStart,
  onRefreshEnd,
}: UseDataRefreshOptions = {}) {
  const { revalidate, state } = useRevalidator();
  const channelRef = useRef(
    typeof window !== "undefined" && "BroadcastChannel" in window
      ? new BroadcastChannel("useDataRefresh")
      : null,
  );

  const stateRef = useRef<RevalidationState>();

  const refresh = useCallback((msg: UseDataRefreshMessage) => {
    const channel = channelRef.current;

    try {
      channel?.postMessage(msg);
    } catch (error) {}
  }, []);

  const handleRefresh = useCallback(
    ({ data }: { data: UseDataRefreshMessage }) => {
      onRefreshStart?.(data);
      revalidate();
      onRefresh?.(data);
    },
    [onRefresh, onRefreshStart, revalidate],
  );

  useEffect(() => {
    // Fire the callback when the refresh is complete
    if (stateRef.current === "loading" && state === "idle") {
      // Omit the message data because there's no reliable
      // way to associate the action with load completion.
      onRefreshEnd?.();
    }

    stateRef.current = state;
  }, [onRefreshEnd, state]);

  useEffect(() => {
    const channel = channelRef.current;

    channel?.addEventListener("message", handleRefresh);

    return () => {
      channel?.removeEventListener("message", handleRefresh);
    };
  }, [handleRefresh]);

  return {
    refresh,
    state,
  };
}
